import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getImageUrl } from '../../lib/utils';
import MediaLibraryModal from '../../components/ui/MediaLibraryModal';
import { MdAdd, MdEdit, MdDelete, MdArticle } from 'react-icons/md';

const emptyForm = {
  title: '',
  excerpt: '',
  content: '',
  image: '',
  author: 'CureHive Nepal',
  status: 'draft'
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/blog?all=true');
      setPosts(data.data || []);
    } catch (error) {
      console.error('Failed to fetch blog posts', error);
      toast.error('Failed to fetch blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingPost) {
        await api.put(`/blog/${editingPost._id}`, formData);
        toast.success('Post updated successfully');
      } else {
        await api.post('/blog', formData);
        toast.success('Post created successfully');
      }
      setIsModalOpen(false);
      fetchPosts();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await api.delete(`/blog/${id}`);
        toast.success('Post deleted successfully');
        fetchPosts();
      } catch (error) {
        console.error('Failed to delete blog post', error);
        toast.error('Failed to delete post');
      }
    }
  };

  const handleImageSelect = (selectedMedia) => {
    if (Array.isArray(selectedMedia) && selectedMedia.length > 0) {
      setFormData((prev) => ({ ...prev, image: selectedMedia[0].url }));
    }
    setIsImageLibraryOpen(false);
  };

  const openModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        image: post.image || '',
        author: post.author || 'CureHive Nepal',
        status: post.status
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData(emptyForm);
  };

  const totalPosts = posts.length;
  const publishedCount = posts.filter((p) => p.status === 'published').length;
  const draftCount = posts.filter((p) => p.status === 'draft').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">
            Blog Management
          </h1>
          <p className="mt-1 text-sm text-admin-slate-600 dark:text-admin-slate-400">
            Create and manage your blog posts to keep your customers informed
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => openModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-admin-ucla-500 hover:bg-admin-ucla-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 dark:focus:ring-offset-admin-slate-800"
          >
            <MdAdd className="w-5 h-5 mr-2" />
            New Post
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-admin-slate-500 dark:text-admin-slate-400">Total Posts</h3>
          <p className="mt-1 text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">{totalPosts}</p>
        </div>
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-admin-slate-500 dark:text-admin-slate-400">Published</h3>
          <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">{publishedCount}</p>
        </div>
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-medium text-admin-slate-500 dark:text-admin-slate-400">Drafts</h3>
          <p className="mt-1 text-2xl font-bold text-yellow-600 dark:text-yellow-400">{draftCount}</p>
        </div>
      </div>

      {/* Posts Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-ucla-600"></div>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white dark:bg-admin-slate-800 rounded-xl shadow-sm p-8">
          <div className="text-center">
            <MdArticle className="mx-auto h-12 w-12 text-admin-slate-400 dark:text-admin-slate-500" />
            <h3 className="mt-2 text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">No posts</h3>
            <p className="mt-1 text-sm text-admin-slate-500 dark:text-admin-slate-400">
              Get started by creating your first blog post
            </p>
            <div className="mt-6">
              <button
                onClick={() => openModal()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-admin-ucla-500 hover:bg-admin-ucla-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 dark:focus:ring-offset-admin-slate-800"
              >
                <MdAdd className="w-5 h-5 mr-2" />
                Create your first post
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-admin-slate-800 rounded-lg shadow border border-admin-slate-200 dark:border-admin-slate-700 overflow-hidden">
          <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
            <thead className="bg-admin-slate-50 dark:bg-admin-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Post</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Author</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-admin-slate-500 dark:text-admin-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              {posts.map((post) => (
                <tr key={post._id} className="hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {post.image ? (
                          <img className="h-10 w-10 rounded-lg object-cover" src={getImageUrl(post.image)} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-admin-slate-200 dark:bg-admin-slate-700 flex items-center justify-center">
                            <MdArticle className="text-admin-slate-500 dark:text-admin-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">{post.title}</div>
                        <div className="text-sm text-admin-slate-500 dark:text-admin-slate-400 line-clamp-1">{post.excerpt}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-admin-slate-500 dark:text-admin-slate-400">
                    {post.author || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${post.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <button onClick={() => openModal(post)} className="text-admin-ucla-600 hover:text-admin-ucla-800 dark:text-admin-ucla-400 dark:hover:text-admin-ucla-300 mr-4 transition-colors">
                      <MdEdit size={18} />
                    </button>
                    <button onClick={() => handleDelete(post._id)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors">
                      <MdDelete size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[70vh] overflow-y-auto">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {editingPost ? 'Edit Post' : 'New Post'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <input type="text" required value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Excerpt</label>
                      <textarea required maxLength={300} value={formData.excerpt} onChange={e => setFormData({ ...formData, excerpt: e.target.value })} rows={2} placeholder="A short summary shown on the blog listing page" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Content</label>
                      <textarea required value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} rows={8} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image</label>
                      <div className="flex items-center gap-3">
                        {formData.image ? (
                          <img src={getImageUrl(formData.image)} alt="Featured" className="w-16 h-16 rounded-md object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-md bg-gray-100 flex items-center justify-center">
                            <MdArticle className="text-gray-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setIsImageLibraryOpen(true)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm"
                        >
                          {formData.image ? 'Change Image' : 'Select Image'}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Author</label>
                      <input type="text" value={formData.author} onChange={e => setFormData({ ...formData, author: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" disabled={saving} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50">
                    {saving ? 'Saving...' : editingPost ? 'Update' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setIsModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <MediaLibraryModal
        isOpen={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        onSelect={handleImageSelect}
        maxSelect={1}
      />
    </div>
  );
};

export default BlogPage;
